import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Pencil, Trash2, Save, X, Star, Eye, EyeOff, Globe } from "lucide-react";

const empty = {
  author: "",
  rating: 5,
  content: "",
  date: new Date().toISOString().slice(0, 10),
  source: "google",
  order: 0,
  is_published: true,
};

const SOURCES = [
  { v: "google", label: "Google" },
  { v: "tripadvisor", label: "Tripadvisor" },
  { v: "thefork", label: "TheFork" },
  { v: "manual", label: "Site / Manuel" },
];

export default function ReviewsAdmin() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [googleUrl, setGoogleUrl] = useState("");
  const [saved, setSaved] = useState(false);

  const load = async () => {
    const [reviews, settings] = await Promise.all([
      api.get("/reviews"),
      api.get("/settings"),
    ]);
    setItems(reviews.data || []);
    setGoogleUrl(settings.data?.["reviews.google_url"] || "");
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = { ...edit, rating: Number(edit.rating) || 5, order: Number(edit.order) || 0 };
    if (edit.id) await api.put(`/reviews/${edit.id}`, body);
    else await api.post("/reviews", body);
    setEdit(null); load();
  };
  const del = async (id) => {
    if (window.confirm("Supprimer cet avis ?")) {
      await api.delete(`/reviews/${id}`); load();
    }
  };
  const togglePublish = async (r) => {
    await api.put(`/reviews/${r.id}`, { ...r, is_published: !r.is_published });
    load();
  };

  const saveGoogleUrl = async () => {
    await api.put("/settings/reviews.google_url", { value: googleUrl });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div data-testid="admin-reviews-page">
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div>
          <p className="overline">Réputation</p>
          <h1 className="font-display text-4xl md:text-5xl mt-3">Avis clients</h1>
          <p className="text-[#697585] mt-2 max-w-xl">Les 5 avis publiés avec le meilleur "ordre" apparaissent dans le carrousel sur la page d'accueil.</p>
        </div>
        <button data-testid="add-review-btn" onClick={() => setEdit({ ...empty })} className="btn-primary">
          <Plus size={14} /> Ajouter un avis
        </button>
      </div>

      <div className="mt-8 border border-[#D9D3C5] bg-[#FFFFFF] p-5">
        <p className="overline">Lien "Voir tous les avis"</p>
        <p className="text-sm text-[#697585] mt-2">URL de votre fiche Google Maps (ou autre). Le bouton apparaît sous le carrousel.</p>
        <div className="mt-4 flex gap-3 flex-wrap">
          <div className="flex-1 min-w-[280px] flex items-center gap-2 border border-[#D9D3C5] px-3">
            <Globe size={14} className="text-[#697585]" />
            <input
              data-testid="reviews-google-url"
              value={googleUrl}
              onChange={(e) => setGoogleUrl(e.target.value)}
              placeholder="https://www.google.com/maps/place/..."
              className="flex-1 bg-transparent py-3 text-sm focus:outline-none"
            />
          </div>
          <button data-testid="reviews-save-url" onClick={saveGoogleUrl} className="btn-primary">
            <Save size={14} /> {saved ? "Enregistré ✓" : "Enregistrer"}
          </button>
        </div>
      </div>

      <div className="mt-8 space-y-4">
        {items.length === 0 && <p className="text-[#697585]">Aucun avis pour l'instant.</p>}
        {items.map((r) => (
          <div key={r.id} data-testid={`review-row-${r.id}`} className="border border-[#D9D3C5] bg-[#FFFFFF] p-5">
            <div className="flex justify-between items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-display text-lg text-[#203240]">{r.author}</span>
                  <span className="inline-flex">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={12} className={i < (r.rating || 0) ? "text-[#B8956A] fill-[#B8956A]" : "text-[#D9D3C5]"} />
                    ))}
                  </span>
                  <span className="text-xs uppercase tracking-[0.18em] text-[#697585]">{r.source}</span>
                  <span className="text-xs text-[#697585]">{r.date}</span>
                  <span className="text-xs text-[#697585]">· ordre {r.order}</span>
                  {!r.is_published && <span className="text-[10px] uppercase tracking-widest text-orange-500 border border-orange-200 px-2 py-0.5">Masqué</span>}
                </div>
                <p className="text-sm text-[#3D4A5A] mt-3 leading-relaxed">« {r.content} »</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => togglePublish(r)} className="p-2 hover:text-[#B8956A]" title={r.is_published ? "Masquer" : "Publier"}>
                  {r.is_published ? <Eye size={14}/> : <EyeOff size={14}/>}
                </button>
                <button onClick={() => setEdit(r)} className="p-2 hover:text-[#B8956A]" data-testid={`review-edit-${r.id}`}>
                  <Pencil size={14}/>
                </button>
                <button onClick={() => del(r.id)} className="p-2 hover:text-red-500" data-testid={`review-delete-${r.id}`}>
                  <Trash2 size={14}/>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
          <div className="bg-[#FFFFFF] border border-[#D9D3C5] w-full max-w-xl max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <h2 className="font-display text-2xl">{edit.id ? "Modifier l'avis" : "Nouvel avis"}</h2>
              <button onClick={() => setEdit(null)}><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <input className="field" placeholder="Auteur *" required value={edit.author} onChange={(e) => setEdit({ ...edit, author: e.target.value })} data-testid="review-input-author" />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-[#697585]">Note (1-5)</label>
                  <input type="number" min={1} max={5} className="field mt-1" value={edit.rating} onChange={(e) => setEdit({ ...edit, rating: e.target.value })} data-testid="review-input-rating" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-[#697585]">Date</label>
                  <input type="date" className="field mt-1" value={edit.date || ""} onChange={(e) => setEdit({ ...edit, date: e.target.value })} />
                </div>
              </div>
              <textarea className="field" rows={5} placeholder="Contenu de l'avis *" required value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })} data-testid="review-input-content" />
              <div className="grid grid-cols-2 gap-4">
                <select className="field" value={edit.source} onChange={(e) => setEdit({ ...edit, source: e.target.value })}>
                  {SOURCES.map((s) => <option key={s.v} value={s.v} className="bg-[#F6F0E7]">{s.label}</option>)}
                </select>
                <input type="number" className="field" placeholder="Ordre (plus haut = en premier)" value={edit.order} onChange={(e) => setEdit({ ...edit, order: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-sm text-[#203240]">
                <input type="checkbox" checked={edit.is_published} onChange={(e) => setEdit({ ...edit, is_published: e.target.checked })} className="accent-[#B8956A]" />
                Publié (visible dans le carrousel)
              </label>
            </div>
            <div className="mt-8 flex gap-3 justify-end">
              <button onClick={() => setEdit(null)} className="btn-ghost">Annuler</button>
              <button onClick={save} data-testid="review-save" className="btn-primary"><Save size={14}/> Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
